import type { SQLiteDatabase } from 'expo-sqlite';

import { now } from '@/db/ids';

/**
 * Whole-database backup / restore. Snapshots every domain table to a JSON
 * blob the user can save and re-import later. Insurance against a lost
 * device or a corrupted OPFS store while cloud sync is still on the
 * roadmap — local-first means the user owns their data and we should make
 * it portable.
 *
 * Schema version is embedded so a restore on a future build can detect
 * mismatches; for now we only restore into the same major version.
 */

const TABLES = [
  'property',
  'system',
  'task',
  'task_completion',
  'document',
  'contact',
  'inventory_item',
  'expense',
  'goal',
  // Wave 45 audit: these three carried real user data but were missing
  // from the bundle — a restore silently dropped them. Restore is
  // tolerant of bundles that predate an entry (restore only touches
  // tables the bundle actually contains), and export records an empty
  // array when a table doesn't exist yet, so adding entries here is
  // always backward + forward compatible.
  'system_reminder',
  'note',
  'quote',
  // Wave 46 audit: the inbox and the settings row (active_property_id,
  // tier) were also missing — a restore onto a new device landed the
  // user on the wrong home with their tier reset.
  'message',
  'user_settings',
] as const;

type TableName = (typeof TABLES)[number];

export interface BackupBundle {
  format: 'homestead-backup';
  version: number;
  schema_version: number;
  exported_at: number;
  tables: Record<TableName, unknown[]>;
}

export async function exportBackup(db: SQLiteDatabase): Promise<BackupBundle> {
  const schemaRow = await db.getFirstAsync<{ user_version: number }>('PRAGMA user_version');
  const tables = {} as Record<TableName, unknown[]>;
  for (const t of TABLES) {
    try {
      tables[t] = await db.getAllAsync(`SELECT * FROM ${t}`);
    } catch (err) {
      // Table might not exist on an older schema — record empty.
      tables[t] = [];
    }
  }
  return {
    format: 'homestead-backup',
    version: 1,
    schema_version: schemaRow?.user_version ?? 0,
    exported_at: now(),
    tables,
  };
}

export type RestoreResult =
  | { ok: true; counts: Record<TableName, number> }
  | { ok: false; error: string };

/**
 * Replaces the contents of every domain table with the bundle's rows.
 * Wrapped in a transaction — either the whole restore lands or none of
 * it does. Disables foreign keys for the duration so import order doesn't
 * matter (children first or parents first both work).
 */
export async function restoreBackup(
  db: SQLiteDatabase,
  bundle: unknown
): Promise<RestoreResult> {
  if (!bundle || typeof bundle !== 'object') {
    return { ok: false, error: 'Not a JSON object.' };
  }
  const b = bundle as Partial<BackupBundle>;
  if (b.format !== 'homestead-backup') {
    return { ok: false, error: "Doesn't look like a Homestead backup." };
  }
  if (!b.tables) {
    return { ok: false, error: 'Backup is missing its data tables.' };
  }

  const counts = {} as Record<TableName, number>;
  try {
    await db.execAsync(`PRAGMA foreign_keys = OFF;`);
    await db.withTransactionAsync(async () => {
      for (const t of TABLES) {
        // Only clear tables the bundle actually carries. An older bundle
        // without (say) user_settings must NOT wipe the current device's
        // settings row and hand back a tier-reset, wrong-active-home app.
        if ((b.tables as Record<string, unknown[]>)[t] === undefined) continue;
        await db.runAsync(`DELETE FROM ${t}`);
      }
      for (const t of TABLES) {
        const rows = (b.tables as Record<string, unknown[]>)[t] ?? [];
        counts[t] = 0;
        for (const row of rows) {
          if (!row || typeof row !== 'object') continue;
          const entries = Object.entries(row as Record<string, unknown>);
          const cols = entries.map(([k]) => k).join(', ');
          const placeholders = entries.map(() => '?').join(', ');
          const values = entries.map(([, v]) => normalizeValue(v));
          await db.runAsync(
            `INSERT INTO ${t} (${cols}) VALUES (${placeholders})`,
            ...values
          );
          counts[t] += 1;
        }
      }
    });
    return { ok: true, counts };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  } finally {
    try {
      await db.execAsync(`PRAGMA foreign_keys = ON;`);
    } catch {
      // Best effort.
    }
  }
}

function normalizeValue(v: unknown): string | number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' || typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  // Objects/arrays land back as JSON strings — matches how we already
  // store *_json columns.
  return JSON.stringify(v);
}

export function backupFilename(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `homestead-backup-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.json`;
}
